import toast from "react-hot-toast";
import DataTable from "@/components/shared/DataTable";
import { Filter, FilterSelect, FilterPill } from "@/components/shared/Filter";
import SearchInput from "@/components/shared/SearchInput";
import Skeleton from "@/components/shared/Skeleton";
import { Badge } from "@/components/ui/Badge";
import UndoPendingTag from "@/components/shared/UndoPendingTag";
import { Card } from "@/components/ui/Card";
import { MatchBar } from "@/components/ui/MatchBar";
import { CandidateService } from "@/services/candidateService";
import CandidateStatusService from "@/services/candidateStatusService";
import { JobService } from "@/services/jobService";
import { TenantSettingsService } from "@/services/tenantSettingsService";
import { formatDate } from "@/utils/dateUtils";
import { shortId } from "@/utils/formatText";
import { getStatusColorIndex } from "@/utils/statusColors";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { scoreFilterOption } from "./constant";
import { Button } from "@/components/ui/Button";
import useLiveResource from "@/hooks/useLiveResource";
import MatchScoreInfo from "./components/MatchScoreInfo";
import { useDebounce } from "@/hooks/useDebounce";
import PermissionGuard from "@/permissions/PermissionGuard";
import ModuleGate from "@/permissions/ModuleGate";
import useModuleAccess from "@/hooks/useModuleAccess";
import { MODULES } from "@/permissions/modules";
import { PERMISSIONS } from "@/permissions/constant";
import { hasPermission, getUserPermissions } from "@/permissions/rbac";
import { useAuth } from "@/hooks/useAuth";
import { ShieldCheck } from "lucide-react";
import CustomTooltip from "@/components/shared/CustomTooltip";
import SendToComplianceModal from "./components/SendToComplianceModal";
import ComplianceEmailModal from "./components/ComplianceEmailModal";
import ChecklistBuilderModal from "@/pages/admin/compliance/checklists/components/ChecklistBuilderModal";
import { ComplianceService } from "@/services/complianceService";
import { getComplianceStatusMeta, COMPLIANCE_STATUS, indexComplianceRunsByApplication, complianceStatusForRow } from "@/pages/admin/compliance/constant";

const BC_CHANNEL = "candidate_sync";

const Candidate = () => {
  const setHeader = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const canSendToCompliance = hasPermission(PERMISSIONS.COMPLIANCE_CREATE, getUserPermissions(user));
  // Entitlement only. Availability is the <ModuleGate> around the button below:
  // a killed module keeps the column and disables the control, because the
  // candidate rows beside it are not compliance data and must not move.
  const { isEnabled: complianceEnabled } = useModuleAccess(MODULES.COMPLIANCE);

  const [searchParams, setSearchParams] = useSearchParams();
  const statusFromURL = searchParams.get("status_id");
  const excludeStatusFromURL = searchParams.get("exclude_status_id");
  // The two the dashboard cards arrive with. Both name a rule the API
  // resolves against the tenant's `is_hired` status flag, so there is no id
  // to resolve here.
  const hiredThisMonth = searchParams.get("hired_this_month") === "true";
  const excludeHired = searchParams.get("exclude_hired") === "true";

  /** Drop URL-borne filters, keeping the rest of the query. */
  const clearParams = useCallback(
    (...keys) => {
      setSearchParams(
        (params) => {
          keys.forEach((key) => params.delete(key));
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState(statusFromURL || "");
  const [minScore, setMinScore] = useState("");
  const [jobFilter, setJobFilter] = useState("");
  const [jobOptions, setJobOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  // Sequence-ordered raw statuses, kept alongside statusOptions' {value,label}
  // shape so the status badge can color by tenant-configured position.
  const [candidateStatusList, setCandidateStatusList] = useState([]);
  const [statusOptionsLoading, setStatusOptionsLoading] = useState(true);
  const [statusOptionsError, setStatusOptionsError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState("all-candidates");
  const [restrictVisibility, setRestrictVisibility] = useState(false);

  const TABS = [
    { id: "all-candidates", name: "All Candidates" },
    { id: "my-candidates", name: "My Candidates" },
  ];

  // Analysing / done / failed, read off the event's *name*. This is the one
  // piece of live state a refetch cannot report: while a run is in flight the
  // stored row still carries the old score and no status at all.
  const [statusOverridesMatch, setStatusOverridesMatch] = useState({});
  const [anyProcessing, setAnyProcessing] = useState(false);
  // Rows read back from the server after an event said they moved. The three
  // maps this replaces — score, status and job title — each held a value
  // forwarded off the wire, and the envelope carries none of them now.
  const [rowOverrides, setRowOverrides] = useState({});

  // Send-to-Compliance: currently selected candidate + modal state, plus the map
  // of candidate compliance statuses to show accurate context in tooltips.
  const [complianceCandidate, setComplianceCandidate] = useState(null);
  // The checklist the send modal asked to show, read-only. Held HERE so the
  // builder is a SIBLING of the send modal rather than nested inside its
  // content — a dialog inside another's creates a stacking context it can
  // never paint above (React error #299).
  const [viewChecklistId, setViewChecklistId] = useState(null);
  const [complianceModalOpen, setComplianceModalOpen] = useState(false);
  // Step 2 of the send. A SIBLING of the send modal, never nested inside it —
  // a dialog in another's content creates a stacking context it can never
  // paint above (React error #299). `complianceEmail` carries the checklist
  // step 1 chose; null means step 2 is closed.
  const [complianceEmail, setComplianceEmail] = useState(null);
  const [complianceStatusMap, setComplianceStatusMap] = useState({});

  const getAppKey = (appId, jobId) => `${appId}_${jobId}`;

  const [refreshKey, setRefreshKey] = useState(0);
  const pendingCandidateIdsRef = useRef(new Set());
  const bcRef = useRef(null);

  // ── Rows read back from the server ───────────────────────────────────────

  /** Re-read one row through the list call this page already makes.
   *
   * `search` is the whole request: the list's search clause casts the
   * candidate, application and job id columns to text, so a full application
   * id matches exactly the row we want. It used to send
   * `{ id, pagination: false }` — `id` is not a parameter of `/candidate/list`
   * and was dropped by FastAPI, and `pagination: false` bypasses the route's
   * per_page cap, so one status change on one watched row downloaded every
   * confirmed application in the tenant and searched it in the browser. */
  const refreshRow = useCallback(async (applicationId) => {
    if (!applicationId) return;
    try {
      const res = await CandidateService.fetCandidateList({
        search: applicationId,
        page: 1,
        per_page: 5,
      });
      const rows = res?.data?.candidates ?? res?.data ?? [];
      const updated = Array.isArray(rows) ? rows.find((r) => String(r.application_id) === String(applicationId)) : rows;
      if (!updated) return;
      setRowOverrides((prev) => ({ ...prev, [getAppKey(updated.application_id, updated.job_id)]: updated }));
    } catch (err) {
      // A row this reader may no longer see, or a transient failure. The rest
      // of the list keeps rendering — dropping one row is the correct answer
      // to `INSUFFICIENT_PERMISSIONS` (§4.4).
      console.error("Failed to refresh candidate row:", err);
    }
  }, []);

  // Applications touched since the last refetch. Collected as events arrive and
  // drained on the debounced `onChange`, so a bulk run that moves five rows
  // costs five row reads at most once each — not one per event.
  const touchedApplicationsRef = useRef(new Set());

  const drainTouchedRows = useCallback(() => {
    const applications = [...touchedApplicationsRef.current];
    touchedApplicationsRef.current.clear();
    applications.forEach((applicationId) => {
      refreshRow(applicationId);
      // The other tabs are told *which* row moved, never what it now says
      // (§4.5). Each one asks the server itself, so two tabs agree because
      // both read the same state rather than because one painted the other's.
      bcRef.current?.postMessage({ key: `application:${applicationId}` });
    });
  }, [refreshRow]);

  const touch = useCallback((applicationId) => {
    if (applicationId) touchedApplicationsRef.current.add(String(applicationId));
  }, []);

  // ── The cross-tab channel ────────────────────────────────────────────────

  useEffect(() => {
    bcRef.current = new BroadcastChannel(BC_CHANNEL);

    let timer = null;
    const pending = new Set();
    bcRef.current.onmessage = (e) => {
      const key = e.data?.key;
      if (!key) return;
      if (key === "table") {
        setRefreshKey((k) => k + 1);
        return;
      }
      const [kind, id] = key.split(":");
      if (kind !== "application" || !id) return;
      // The same 200 ms window the socket path uses, so a burst in the other
      // tab costs this one the same single read.
      pending.add(id);
      clearTimeout(timer);
      timer = setTimeout(() => {
        [...pending].forEach((applicationId) => refreshRow(applicationId));
        pending.clear();
      }, 200);
    };

    return () => {
      clearTimeout(timer);
      bcRef.current?.close();
      bcRef.current = null;
    };
  }, [refreshRow]);

  const handleClearAll = () => {
    setStatusFilter("");
    setMinScore("");
    setJobFilter("");
    // The dashboard cards' filters live in the URL rather than in state, and
    // they show a chip each — "Clear all" that left them standing would be
    // saying something untrue.
    clearParams("hired_this_month", "exclude_hired");
  };

  // ── Live ─────────────────────────────────────────────────────────────────

  // The match runs of every candidate this reader can see. `match.*` carries
  // the candidate as `id` and `{application, job}` as `parent`, so the row it
  // belongs to is the parent application.
  useLiveResource({
    keys: "match",
    onEvent: (event) => {
      const applicationId = event.parent?.application;
      const jobId = event.parent?.job;
      if (!applicationId || !jobId) return;
      const appKey = getAppKey(applicationId, jobId);

      if (event.event === "match.started") {
        pendingCandidateIdsRef.current.add(appKey);
        setAnyProcessing(true);
        setStatusOverridesMatch((prev) => ({ ...prev, [appKey]: "IN_PROGRESS" }));
        return;
      }

      const terminal = event.event === "match.ready" ? "COMPLETED" : "FAILED";
      setStatusOverridesMatch((prev) => ({ ...prev, [appKey]: terminal }));
      pendingCandidateIdsRef.current.delete(appKey);
      if (pendingCandidateIdsRef.current.size === 0) setAnyProcessing(false);
      if (event.event === "match.ready") touch(applicationId);
    },
    onChange: drainTouchedRows,
  });

  // A candidate or an application changing. `application.status_changed`
  // carries the application as `id`; `candidate.resume_updated` carries the
  // candidate, with the application under `parent`.
  useLiveResource({
    keys: ["application", "candidate", "bulk_batch"],
    onEvent: (event) => {
      // A new row, or a batch of them: the list itself has to be re-read, not
      // one row of it.
      if (event.event === "application.created" || event.event === "bulk_batch.confirmed" || event.event === "candidate.reupload_confirmed") {
        setRefreshKey((k) => k + 1);
        bcRef.current?.postMessage({ key: "table" });
        return;
      }
      touch(event.resource === "application" ? event.id : event.parent?.application);
    },
    onChange: drainTouchedRows,
  });

  // A job's title shows in the "Applied for" column, so a renamed job means
  // re-reading the page. The title itself is not on the envelope.
  useLiveResource({
    keys: "job",
    onChange: () => {
      setRefreshKey((k) => k + 1);
      bcRef.current?.postMessage({ key: "table" });
    },
  });

  // The org's data-visibility mode and the consent registry are edited on
  // another screen (Organization Settings) and change what this one shows.
  // `tenant_config.updated` is unfiltered — it arrives whether or not this
  // connection asked for the key — and the subscription is sent anyway so
  // every call site in the app reads the same way. Without this the page read
  // the settings once on mount: the admin flipped PRIVATE mode, and every
  // other tab kept showing the old rules until someone reloaded (G1).
  useLiveResource({
    keys: "tenant_config",
    // Wrapped rather than passed: `fetchSettings` is declared further down.
    onChange: () => fetchSettings(),
  });

  const COLUMNS = [
    {
      key: "full_name",
      label: "Candidate",
      sortable: true,
      render: (val, row) => {
        const appKey = getAppKey(row.application_id, row.job_id);
        const r = rowOverrides[appKey] ?? row;

        return (
          <div className="flex flex-col items-start gap-0.5">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/candidate-analysis/${r.id}?job_id=${row.job_id}`);
              }}
              className="text-left text-text-primary hover:text-text-blue font-medium text-[13px] p-0"
            >
              {r.full_name ?? val}
            </Button>
          </div>
        );
      },
    },
    {
      key: "job_title",
      label: "Jobs",
      sortable: true,
      render: (val, row) => {
        const appKey = getAppKey(row.application_id, row.job_id);
        const r = rowOverrides[appKey] ?? row;

        return (
          <div className="flex flex-col items-start gap-0.5">
            <span className="text-text-primary text-[12px]">{r.job_title ?? val}</span>
          </div>
        );
      },
    },
    {
      key: "match_score",
      label: (
        <div className="flex items-center gap-1">
          <span>Match Indicator</span>
          <MatchScoreInfo />
        </div>
      ),
      sortable: true,
      render: (val, row) => {
        const appKey = getAppKey(row.application_id, row.job_id);
        const r = rowOverrides[appKey] ?? row;
        const score = r.match_score ?? val;
        const matchStatus = statusOverridesMatch[appKey] ?? r.match_status;

        return <MatchBar score={score != null ? score / 10 : null} status={matchStatus} />;
      },
    },
    {
      key: "total_experience",
      label: "Experience",
      sortable: true,
      align: "center",
      render: (val, row) => {
        const appKey = getAppKey(row.application_id, row.job_id);
        const r = rowOverrides[appKey] ?? row;

        return <span className="text-text-primary text-[12px]">{r.total_experience ?? val ?? "-"}</span>;
      },
    },
    {
      key: "notice_period",
      label: "Notice Period",
      sortable: true,
      align: "center",
      render: (val, row) => {
        const appKey = getAppKey(row.application_id, row.job_id);
        const r = rowOverrides[appKey] ?? row;

        return <span className="text-text-primary text-[12px]">{r.notice_period ?? val ?? "-"}</span>;
      },
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (val, row) => {
        const appKey = getAppKey(row.application_id, row.job_id);
        const r = rowOverrides[appKey] ?? row;
        const status = r.status ?? val;

        return (
          <div className="flex flex-col items-center">
            <Badge
              label={status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : "—"}
              badgeStyle="text-[11px]"
              variant="gradient"
              colorIndex={getStatusColorIndex(status, candidateStatusList)}
              className="h-6 border-[1px] min-w-[88px] max-w-full whitespace-nowrap text-center rounded-[2rem]"
            />
            {r.undo_request_pending && <UndoPendingTag />}
          </div>
        );
      },
    },
    {
      key: "applied_at",
      label: "Applied Date",
      sortable: true,
      align: "center",
      render: (val, row) => {
        const appKey = getAppKey(row.application_id, row.job_id);
        const r = rowOverrides[appKey] ?? row;

        return <span className="text-text-primary text-[12px]">{formatDate(r.applied_at ?? val)}</span>;
      },
    },
    // Only built when the user can act on it — a COMPLIANCE_VIEW-only user must see
    // no Actions column at all (header + cells), not just a hidden button (D11/Phase J).
    ...(canSendToCompliance
      ? [
          {
            key: "compliance_actions",
            label: "Actions",
            sortable: false,
            align: "center",
            render: (_val, row) => {
              const appKey = getAppKey(row.application_id, row.job_id);
              const r = rowOverrides[appKey] ?? row;
              // This row IS an application, so its own application id is the
              // only thing that answers "has THIS been sent to compliance"
              // (Phase G). Matching on the person — id, email or name — is
              // what gave a candidate on two jobs one answer for both.
              const complianceStatus = complianceStatusForRow(complianceStatusMap, r) ?? complianceStatusForRow(complianceStatusMap, row);
              const alreadySent = Boolean(complianceStatus);
              // The send route refuses a rejected application, so the button does too.
              const isRejected = Boolean(r.status_is_rejected);

              let tooltipText = "Send to Compliance";
              if (alreadySent) {
                const meta = complianceStatus ? getComplianceStatusMeta(complianceStatus) : null;
                tooltipText = meta?.label ? `Already in Compliance (${meta.label})` : "Already in Compliance";
              } else if (isRejected) {
                tooltipText = "Rejected candidates cannot be sent to compliance";
              }

              // Phase S2: no stage test. Compliance can start from anywhere in
              // the pipeline; only a run this application already has, or a
              // rejection, closes the action.
              const isActionable = !alreadySent && !isRejected;

              return (
                <div className="flex gap-2 justify-center">
                  <ModuleGate module={MODULES.COMPLIANCE}>
                    <CustomTooltip text={tooltipText}>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isActionable) return;
                          setComplianceCandidate(row);
                          setComplianceModalOpen(true);
                        }}
                        disabled={!isActionable}
                        className="px-3.5 py-2.5 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-background-icon transition-none"
                      >
                        <ShieldCheck size={16} />
                      </Button>
                    </CustomTooltip>
                  </ModuleGate>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  useEffect(() => {
    setHeader({
      title: "Candidates List",
      subTitle: loading ? <Skeleton className="h-5 w-28" /> : `Total ${total} candidates`,
    });

    return () => setHeader({ title: "", subTitle: "" });
  }, [loading, total, setHeader]);

  const fetchJobs = async () => {
    try {
      const response = await JobService.fetchAdminJobDetails({
        pagination: false,
      });

      const options = (response?.data?.jobs || []).map((j) => ({
        value: j.id,
        label: (
          <span className="flex w-full items-baseline justify-start gap-1 text-left">
            <span className="min-w-0 flex-1 truncate text-left">{j.title}</span>
            <span className="shrink-0 text-text-secondary font-mono text-[11px]">({shortId(j.id)})</span>
          </span>
        ),
        title: j.title,
      }));

      setJobOptions(options);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load job filters.");
    }
  };

  const fetchCandidateStatuses = async () => {
    try {
      setStatusOptionsLoading(true);
      setStatusOptionsError("");
      const response = await CandidateStatusService.list({
        page: 1,
        per_page: 100,
        sort_by: "sequence",
        sort_order: "asc",
      });
      const statuses = Array.isArray(response?.data) ? response.data : Array.isArray(response?.data?.data) ? response.data.data : [];
      const activeStatuses = statuses.filter((status) => status?.id && status.is_active !== false);

      setCandidateStatusList(activeStatuses);

      // Candidate list filters accept status UUIDs, while names remain the
      // useful label shown to recruiters.
      setStatusOptions(
        activeStatuses.map((status) => ({
          value: String(status.id),
          label: status.name || status.code || "Unnamed status",
        })),
      );
    } catch (error) {
      console.error("Failed to load candidate statuses:", error);
      setStatusOptions([]);
      setCandidateStatusList([]);
      setStatusOptionsError("Status filters unavailable");
      toast.error("Failed to load candidate statuses.");
    } finally {
      setStatusOptionsLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await TenantSettingsService.getSettings();
      if (response?.data) {
        setRestrictVisibility(Boolean(response.data.restrictDataVisibility));
      }
    } catch (error) {
      console.error("Failed to load settings:", error);
      toast.error("Failed to load organization settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchSettings();
    fetchCandidateStatuses();
  }, []);

  // Who is already in compliance, so a row can say so (Phase G). The
  // trigger-stage read that used to sit beside this went with Phase S2 — there
  // is no configured stage to gate on any more.
  //
  // `complianceEnabled` as well as the permission: these routes sit behind the
  // module's router prefix, so a tenant without Compliance gets a 403. Permission
  // filtering already stops that for a normal role, which owns `compliance.create`
  // and loses it with the module — but a role holding the `*` wildcard matches
  // every code and keeps firing this request. Not entitled means no fetch.
  useEffect(() => {
    if (!canSendToCompliance || !complianceEnabled) return;
    ComplianceService.getCandidates({ pagination: false })
      .then((res) => {
        const list = res?.data?.candidates ?? res?.candidates ?? [];
        // Keyed on the owning application, and on nothing else (Phase G).
        //
        // This previously indexed by candidate id, lowercased email AND
        // lowercased display name, then read back through five fallbacks. Two
        // applications of one candidate therefore shared one answer, so a
        // candidate approved for one job showed "Already in Compliance" on the
        // job nobody had sent them to — and two different people who happen to
        // share a name shared a compliance status.
        setComplianceStatusMap(indexComplianceRunsByApplication(list));
      })
      .catch((err) => console.error("Failed to load compliance candidates", err));
  }, [canSendToCompliance, complianceEnabled]);
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, minScore, jobFilter, pageSize, activeTab]);

  const handleDataLoaded = (rows) => {
    const pending = new Set((rows ?? []).filter((r) => r.match_score == null && r.match_status === "IN_PROGRESS").map((r) => getAppKey(r.application_id, r.job_id)));

    pendingCandidateIdsRef.current = pending;
    setAnyProcessing(pending.size > 0);

    const appKeys = new Set((rows ?? []).map((r) => getAppKey(r.application_id, r.job_id)));

    // Drop overrides for rows that left the page: the fresh page already
    // carries their current state.
    setRowOverrides((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => appKeys.has(k))));

    setStatusOverridesMatch((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => appKeys.has(k))));
  };

  const filters = useMemo(
    () => [
      {
        key: "status",
        label: statusOptionsLoading ? "Loading statuses..." : statusOptionsError || "All Statuses",
        value: statusFilter,
        onChange: setStatusFilter,
        options: statusOptions,
      },
      {
        key: "score",
        label: "Match Indicator",
        value: minScore,
        onChange: setMinScore,
        options: scoreFilterOption,
      },
      {
        key: "job_id",
        label: "All Jobs",
        value: jobFilter,
        onChange: setJobFilter,
        options: jobOptions,
      },
    ],
    [statusFilter, minScore, jobFilter, jobOptions, statusOptions, statusOptionsLoading, statusOptionsError],
  );

  const activeFilters = useMemo(() => {
    const selectedJob = jobOptions.find((job) => job.value === jobFilter);
    const selectedStatus = statusOptions.find((s) => s.value === statusFilter);
    const selectedScore = scoreFilterOption.find((s) => s.value === minScore);

    return [
      statusFilter && {
        key: "status",
        label: `Status: ${selectedStatus?.label ?? (statusOptionsLoading ? "Loading status..." : "Selected status")}`,
        clear: () => setStatusFilter(""),
      },
      minScore && {
        key: "score",
        label: `Score: ${selectedScore?.label}`,
        clear: () => setMinScore(""),
      },
      jobFilter && {
        key: "job_id",
        label: `Job: ${selectedJob?.title}`,
        clear: () => setJobFilter(""),
      },
      // Arriving from a dashboard card, the only filter narrowing the list
      // lives in the URL. Without a chip the list reads as unfiltered and the
      // reader cannot tell why the count is short, or undo it.
      hiredThisMonth && {
        key: "hired_this_month",
        label: "Hired this month",
        clear: () => clearParams("hired_this_month"),
      },
      excludeHired && {
        key: "exclude_hired",
        label: "Excluding hired",
        clear: () => clearParams("exclude_hired"),
      },
    ].filter(Boolean);
  }, [statusFilter, minScore, jobFilter, jobOptions, statusOptions, statusOptionsLoading, hiredThisMonth, excludeHired, clearParams]);

  const extraParams = useMemo(
    () => ({
      status_id: statusFilter || undefined,
      exclude_status_id: excludeStatusFromURL || undefined,
      score_min: minScore || undefined,
      job_id: jobFilter || undefined,
      search: debouncedSearch || undefined,
      page: currentPage,
      per_page: pageSize,
      pagination: true,
      hired_this_month: hiredThisMonth || undefined,
      exclude_hired: excludeHired || undefined,
      mine: activeTab === "my-candidates" || undefined,
    }),
    [excludeStatusFromURL, statusFilter, minScore, jobFilter, currentPage, pageSize, debouncedSearch, hiredThisMonth, excludeHired, activeTab],
  );

  useEffect(() => {
    if (location.state?.jobId) {
      setJobFilter(location.state.jobId);
    }
  }, [location.state]);

  return (
    <PermissionGuard permission={PERMISSIONS.CANDIDATE_VIEW}>
      <div className="flex flex-col gap-2.5" style={{ zoom: 0.95 }}>
        <div className="flex items-center gap-2.5">
          <div className="flex-1">
            <SearchInput value={search} onChange={setSearch} placeholder="Search by name, job name, candidate ID, application ID, or job ID" />
          </div>

          <Filter
            filters={filters}
            activeFilters={activeFilters}
            showFilters={showFilters}
            onToggle={() => setShowFilters((prev) => !prev)}
            onClearAll={handleClearAll}
            renderFilter={(f) => <FilterSelect key={f.key} {...f} open={f.open} onToggle={f.onToggle} />}
            renderActiveFilter={(f) => <FilterPill key={f.key} label={f.label} onRemove={f.clear} />}
          />
        </div>

        {!restrictVisibility && (
          <div className="flex items-center w-fit bg-background-tab border border-border-primary rounded-xl px-1.5 py-1.5 shrink-0 gap-3">
            {TABS.map((tab) => (
              <Button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center justify-center transition-all duration-150
                  gap-1.5 px-3 py-1 rounded-lg font-medium text-[13px] whitespace-nowrap
                  ${
                    activeTab === tab.id
                      ? "bg-background-tertiary text-text-primary border border-white/10 shadow-sm"
                      : "border border-transparent text-text-secondary hover:text-text-primary"
                  }
                `}
              >
                <span>{tab.name}</span>
              </Button>
            ))}
          </div>
        )}

        {anyProcessing && !loading && (
          <div className="flex items-center">
            <div
              className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold"
              style={{
                background: "rgba(99,102,241,0.1)",
                border: "1px solid rgba(99,102,241,0.2)",
                color: "#818cf8",
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#818cf8] inline-block" />
              Processing scores…
            </div>
          </div>
        )}

        <Card className="!p-0 bg-table-heading border-border-primary overflow-hidden rounded-xl">
          <DataTable
            key={`${jobFilter}-${refreshKey}-${debouncedSearch}-${currentPage}-${pageSize}`}
            rowKey="application_id"
            api={CandidateService.fetCandidateList}
            columns={COLUMNS}
            setTotal={setTotal}
            setLoading={setLoading}
            extraParams={extraParams}
            onRowClick={(row) => navigate(`/admin/candidate-analysis/${row.id}?job_id=${row.job_id}`)}
            onDataLoaded={handleDataLoaded}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            paginationLabel="candidates"
          />
        </Card>

        <SendToComplianceModal
          open={complianceModalOpen && !viewChecklistId && !complianceEmail}
          candidate={complianceCandidate}
          // The row IS an application, so it carries both. Phase E resolves
          // the job's default checklist from `jobId`; `applicationId` is what
          // records who owns the resulting run (Phase C). A row missing either
          // simply falls back to the manual picker.
          jobId={complianceCandidate?.job_id}
          jobTitle={complianceCandidate?.job_title}
          applicationId={complianceCandidate?.application_id}
          onViewChecklist={setViewChecklistId}
          // Close-then-reopen, the same shape the checklist viewer uses:
          // `complianceModalOpen` stays true, so Back in step 2 brings this
          // modal straight back with the checklist it resolved.
          onNext={({ checklistId }) => setComplianceEmail({ checklistId })}
          onClose={() => {
            setComplianceModalOpen(false);
            setComplianceCandidate(null);
          }}
          onSent={() => {
            // Keyed on the application just sent, matching the index this map
            // is read through (Phase G). Keying on the candidate marked every
            // row of that person as sent, including the applications nobody
            // had sent — the optimistic half of the same bug.
            const applicationId = complianceCandidate?.application_id;
            if (!applicationId) return;
            setComplianceStatusMap((prev) => ({
              ...prev,
              [String(applicationId)]: COMPLIANCE_STATUS.PENDING_FROM_CANDIDATE,
            }));
          }}
        />

        <ComplianceEmailModal
          open={!!complianceEmail && !viewChecklistId}
          candidate={complianceCandidate}
          applicationId={complianceCandidate?.application_id}
          checklistId={complianceEmail?.checklistId}
          onBack={() => setComplianceEmail(null)}
          onClose={() => {
            setComplianceEmail(null);
            setComplianceModalOpen(false);
            setComplianceCandidate(null);
          }}
          onSent={() => {
            // Keyed on the application just sent, matching the index this map
            // is read through (Phase G) — the same rule step 1's onSent keeps.
            const applicationId = complianceCandidate?.application_id;
            if (!applicationId) return;
            setComplianceStatusMap((prev) => ({
              ...prev,
              [String(applicationId)]: COMPLIANCE_STATUS.PENDING_FROM_CANDIDATE,
            }));
          }}
        />

        {/* Close-then-reopen: the send modal is withheld while this is up and
            returns when it closes, because `complianceModalOpen` was never
            cleared. `readOnly` makes the builder the Checklist Details screen,
            so there is no second view modal to keep in step. */}
        {viewChecklistId && <ChecklistBuilderModal open readOnly checklistId={viewChecklistId} onClose={() => setViewChecklistId(null)} />}
      </div>
    </PermissionGuard>
  );
};

export default Candidate;
